import {
  CartDetailResponse,
  CartProductResponse,
  GetCartDetailResponse,
} from '@/data/api/model/cart/CartDto';
import { BuyerResponse } from '@/data/api/model/draftOrder/SearchBuyers';
import { ProductResponse } from '@/data/api/model/draftOrder/SearchProducts';
import { enforce2Decimals, formatDollarAmount } from '@/utils/amounts';
import { firstOrNull } from '@/utils/functions';
import dayjs from 'dayjs';
import {
  CartBuyerUi,
  ProductUi,
  SellerLineUi,
  TotalTicketUi,
} from './CartUiModel';
import { CreateEditCartUi } from './CreateEdit/CreateEditUiModel';
import { ViewCartUi } from './View/ViewCartUiModel';

export function buyerResponseToCartBuyerui(
  buyerResponse: BuyerResponse
): CartBuyerUi {
  return {
    id: buyerResponse.business_id,
    name: buyerResponse.business_name,
  };
}

export function productResponseToProductUi(
  productResponse: ProductResponse
): ProductUi {
  return {
    id: productResponse.product_id,
    skuId: productResponse.sku_id,
    sellerId: productResponse.seller.business_id,
    sellerName: productResponse.seller.business_name,
    sellerMinAmount: productResponse.seller.business_min_order_amount,
    imageUrl: firstOrNull(productResponse.product_images) ?? '',
    productName: productResponse.name,
    pricingLabel: pricingLabel(
      productResponse.price,
      productResponse.unit_name
    ),
    customPricing: `${enforce2Decimals(productResponse.price)}`,
    quantity: productResponse.initial_sale_quantity,
    quantityStep: productResponse.sale_steps ?? 1,
    unitName: productResponse.unit_name,
    selectorUnitName: productResponse.selector_unit_name,
    averageWeight: productResponse.avg_weight,
    presentation: productResponse.presentation,
    total: '',
  };
}

export const cartDetailApiResponseToCreateEditCart = (
  input: GetCartDetailResponse
): CreateEditCartUi => ({
  buyer: {
    id: input.buyer_business_id,
    name: input.buyer_name,
  },
  products: input.cart_details.map(cartDetailResponseToProductsUi).flat(),
  sellers: input.cart_details.map(cartDetailResponseToSellersUi),
  total: getCartDetailResponseToTotalTicketUi(input),
  cartTitle: input.cart_name ?? '',
});

export const cartDetailApiResponseToViewCartUi = (
  input: GetCartDetailResponse
): ViewCartUi => ({
  buyer: {
    id: input.buyer_business_id,
    name: input.buyer_name,
  },
  products: input.cart_details.map(cartDetailResponseToProductsUi).flat(),
  sellers: input.cart_details.map(cartDetailResponseToSellersUi),
  total: getCartDetailResponseToTotalTicketUi(input),
});

function cartDetailResponseToSellersUi(
  cartDetailResponse: CartDetailResponse
): SellerLineUi {
  const deliveryDate = dayjs(
    cartDetailResponse.delivery.delivery_date,
    'YYYY-MM-DD'
  );
  const deliveryStartTime = dayjs(
    cartDetailResponse.delivery.delivery_time,
    'HH:mm'
  );
  const deliveryEndTime = dayjs(
    cartDetailResponse.delivery.delivery_time_end,
    'HH:mm'
  );
  return {
    id: cartDetailResponse.seller_id,
    name: cartDetailResponse.business_name,
    total: `${formatDollarAmount(cartDetailResponse.total_amount)}`,
    minimumOrderAmount: `${formatDollarAmount(
      cartDetailResponse.minimum_order_amount
    )}`,
    deliveryDate: deliveryDate.isValid() ? deliveryDate : null,
    deliveryStartTime: deliveryStartTime.isValid() ? deliveryStartTime : null,
    deliveryEndTime: deliveryEndTime.isValid() ? deliveryEndTime : null,
    deliveryNotes: cartDetailResponse.delivery.delivery_notes ?? '',
  };
}

function cartDetailResponseToProductsUi(
  cartDetailResponse: CartDetailResponse
): ProductUi[] {
  return cartDetailResponse.cart_items.map((cartProductItem) =>
    cartProductItemResponseToProductUi(
      cartProductItem,
      cartDetailResponse.seller_id,
      cartDetailResponse.business_name,
      cartDetailResponse.minimum_order_amount
    )
  );
}

function cartProductItemResponseToProductUi(
  input: CartProductResponse,
  sellerId: string,
  sellerName: string,
  sellerMinAmount: number
): ProductUi {
  return {
    id: input.product_id,
    skuId: input.sku_id,
    imageUrl: firstOrNull(input.product_image_url) ?? '',
    sellerId: sellerId,
    sellerName: sellerName,
    sellerMinAmount: sellerMinAmount,
    productName: input.product_name,
    pricingLabel: pricingLabel(input.original_price_amount, input.unit_name),
    presentation: input.presentation,
    customPricing: `${input.unit_price_amount}`,
    quantity: input.quantity,
    quantityStep: input.sale_steps,
    unitName: input.unit_name,
    selectorUnitName: input.selector_unit_name,
    averageWeight: input.avg_weight,
    total: `${formatDollarAmount(input.total_cart_item_amount)}`,
  };
}

function pricingLabel(unitPrice: number, unitName: string) {
  return `$${unitPrice}/${unitName}`;
}

export function producUiToSearchResultLabel(product: ProductUi) {
  return `${product.productName} - ${product.sellerName} - ${product.pricingLabel} `;
}

const getCartDetailResponseToTotalTicketUi = (
  input: GetCartDetailResponse
): TotalTicketUi => ({
  total: `${formatDollarAmount(input.total_amount)}`,
});
